/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Sticky from 'react-stickynode';
import {
  Button,
  Divider,
  ColorPicker,
  Grid,
  GridCell,
  Input,
  langs,
  PageSection,
  Spacer,
  Stack,
  IconButton,
  Icon,
  TextSubtitle,
  TextBody,
  BlockList,
  RadioGroup,
  Radio,
  logos,
  Image,
  TextCaption,
  Filepond,
  SetProfilePicture,
  EntityTypes,
  PortalDestination,
  Snackbar,
} from '@pypestream/design-system';
import { AgentRouting } from '@pypestream/api-services/urql';
import PypeLogo from '@pypestream/design-system/assets/pypestream-logo-black.svg?react';
import { throwErrorWithId } from '@pypestream/utils';

import { capitalizeWords, uploadFile } from '../../utils';
import { useProStudioRoutes } from '../../hooks';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { useEditorModal } from '../modals';
import { PypePreview } from './pype-preview';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

export type ChannelsDetailsFormValues = {
  name: string;
  env: string;
  pypeId: string;
  streamId: string;
  id?: string;
};

export type BrandingOptionsFormValues = {
  widgetStyleColor: string;
  widgetStyleTextColor: string;
  agentBubbleColor: string;
  agentBubbleTextColor: string;
  userBubbleColor: string;
  userBubbleTextColor: string;
  buttonColor: string;
  textColor: string;
  name: string;
  bannerUrl: string;
  customPypeCss: string;
  brandMomentUrl: string;
  logo: string;
};

export type SizingOptionsFormValues = {
  widgetWidth: number;
  widgetHeight: number;
  shouldLockRatio: boolean;
};

export type AgentSupportPlatformFormValues = {
  accountId: string;
  inboxId: string;
  agentRouting: AgentRouting;
  botRetry: {
    attempts: number;
    timeout: number;
  };
};

export interface DefaultFormValues {
  detailsFormValues?: ChannelsDetailsFormValues;
  brandingFormValues?: BrandingOptionsFormValues;
  sizingFormValues?: SizingOptionsFormValues;
  agentSupportPlatformFormValues?: AgentSupportPlatformFormValues;
}

interface ChannelSettingsFormProps {
  type: 'create' | 'update';
  values: DefaultFormValues;
}

// @TODO: JSON validation function from BOT-MANAGER-FRONTEND
// const validateJson = (stringifiedJson: string) => {
//   if (stringifiedJson) {
//     try {
//       JSON.parse(stringifiedJson);
//     } catch (e) {
//       return false;
//     }
//     return true;
//   }
//   return true;
// };

// @TODO: use it for color validation
// https://github.com/scttcper/tinycolor

const getWidthToHeightRatio = (width: number, height: number) => {
  let widthToHeightRatio = (width || 0) / (height || 0);
  if (isNaN(widthToHeightRatio) || widthToHeightRatio === Infinity) {
    widthToHeightRatio = 1;
  }
  return widthToHeightRatio;
};

export const ChannelSettingsForm: FC<ChannelSettingsFormProps> = ({
  type,
  values,
}) => {
  const [t] = useTranslation();
  const [widthToHeightRatio, setWidthToHeightRatio] = useState(1);
  const { projectId, botId, uniqueCustomerId, envs } = useProStudioCtxSelector(
    (ctx) => ({
      envs: ctx.envs,
      projectId: ctx.project?.id,
      uniqueCustomerId: ctx.project?.projectProStudioSetting?.customerId,
      botId:
        ctx.customerId && ctx.botName
          ? `${ctx.customerId}.${ctx.botName}`
          : undefined,
    })
  );

  const routes = useProStudioRoutes();
  const navigate = useNavigate();
  const isUpdating = type === 'update';

  const detailsForm = useForm<ChannelsDetailsFormValues>({
    defaultValues: values.detailsFormValues || {},
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const brandingForm = useForm<BrandingOptionsFormValues>({
    defaultValues: values.brandingFormValues,
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const sizingForm = useForm<SizingOptionsFormValues>({
    defaultValues: values.sizingFormValues,
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const agentSupportPlatformForm = useForm<AgentSupportPlatformFormValues>({
    defaultValues: values.agentSupportPlatformFormValues,
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const { editorModal: cssEditorModal, openModal: openCssEditorModal } =
    useEditorModal({
      title: 'Custom CSS',
      langs: [langs.css()],
      code: brandingForm.getValues('customPypeCss'),
      onSave: (fileData) => {
        brandingForm.setValue('customPypeCss', fileData, { shouldDirty: true });
      },
    });

  const formTouched = detailsForm.formState.isDirty;
  const brandingsTouched = brandingForm.formState.isDirty;
  const sizingTouched = sizingForm.formState.isDirty;
  const agentSupportPlatformTouched =
    agentSupportPlatformForm.formState.isDirty;
  const touched =
    formTouched ||
    brandingsTouched ||
    sizingTouched ||
    agentSupportPlatformTouched;

  const handleFormReset = () => {
    detailsForm.reset();
    brandingForm.reset();
    sizingForm.reset();
    agentSupportPlatformForm.reset();
  };

  const onCancel = () => {
    handleFormReset();
    navigate(routes.channels.path);
  };

  const onSubmit = () => {
    const detailsValid = detailsForm.trigger();
    const brandingValid = brandingForm.trigger();
    const sizingValid = sizingForm.trigger();
    const agentSupportValid = agentSupportPlatformForm.trigger();

    Promise.all([
      detailsValid,
      brandingValid,
      sizingValid,
      agentSupportValid,
    ]).then(
      ([isDetailsValid, isBrandingValid, isSizingValid, isAgentValid]) => {
        if (
          !isDetailsValid ||
          !isBrandingValid ||
          !isSizingValid ||
          !isAgentValid
        ) {
          return;
        }

        const channelDetailsData = detailsForm.getValues();
        const brandingData = brandingForm.getValues();
        const sizingData = sizingForm.getValues();
        const agentSupportPlatformData = agentSupportPlatformForm.getValues();

        const cb = () => {
          navigate(routes.channels.path);
          handleFormReset();
        };

        const commonProps = {
          name: channelDetailsData.name,
          pypeId: channelDetailsData.pypeId,
          streamId: channelDetailsData.streamId,
          env: channelDetailsData.env as 'live' | 'sandbox',
          style: {
            name: brandingData.name,
            shouldLockRatio: sizingData.shouldLockRatio,
            widgetHeight: +sizingData.widgetHeight,
            widgetWidth: +sizingData.widgetWidth,
            widgetStyleColor: brandingData.widgetStyleColor,
            widgetStyleTextColor: brandingData.widgetStyleTextColor,
            agentBubbleColor: brandingData.agentBubbleColor,
            agentBubbleTextColor: brandingData.agentBubbleTextColor,
            userBubbleColor: brandingData.userBubbleColor,
            userBubbleTextColor: brandingData.userBubbleTextColor,
            bannerUrl: brandingData.bannerUrl,
          },
          beta: {
            customStyling: {
              brandMomentUrl: brandingData.brandMomentUrl,
              buttonColor: brandingData.buttonColor,
              textColor: brandingData.textColor,
            },
          },
          config: {
            agent_routing: agentSupportPlatformData.accountId
              ? AgentRouting.ExternalAgent
              : agentSupportPlatformData.agentRouting,
            bot_retry: agentSupportPlatformData.botRetry,
            config: {
              integration_headers: {
                account_id: agentSupportPlatformData.accountId,
                inbox_id: agentSupportPlatformData.inboxId,
              },
            },
          },
          customPypeCss: brandingData.customPypeCss,
          cb,
        };

        const props = isUpdating
          ? {
              type: 'updateChannel' as const,
              payload: {
                id: channelDetailsData.id || '',
                ...commonProps,
              },
            }
          : {
              type: 'createChannel' as const,
              payload: {
                ...commonProps,
              },
            };

        sendProStudioEvent(props);
      }
    );
  };

  const getOnUpload = (fileType: 'logo' | 'assetData') => {
    const onUpload: SetProfilePicture = ({
      entityId,
      file,
      load,
      error,
      progress,
      abort,
      onChange,
    }) => {
      if (!projectId) {
        throwErrorWithId(
          t('pro-studio/channel-details:form.validation-messages.projectId') ||
            'Project ID is required'
        );
      }

      const env = detailsForm.getValues('env');

      if (fileType === 'logo' && !env) {
        throwErrorWithId(
          t('pro-studio/channel-details:form.validation-messages.env') ||
            'Environment is required'
        );
      }

      if (fileType === 'logo' && !uniqueCustomerId) {
        throwErrorWithId(
          t('pro-studio/channel-details:form.validation-messages.customerId') ||
            'Customer ID is required'
        );
      }

      const urlPart = {
        logo: `/rest/pypes/${projectId}/${env}/${uniqueCustomerId}/${entityId}/logo`,
        assetData: `/rest/bot-manager/${projectId}/${entityId}/assets`,
      };

      const uploadUrl = `${import.meta.env.FE_ADMIN_URL}${urlPart[fileType]}`;

      return uploadFile({
        url: uploadUrl,
        file: file as File,
        type: fileType,
        load,
        progress,
        abort,
        onChange: (props) => {
          onChange?.({
            ...props,
            // eslint-disable-next-line react/prop-types
            pictureId: props.files?.[0]?.name,
          });
        },
        error: (e) => {
          sendProStudioEvent({
            type: 'showError',
            error: e,
          });
          error?.(e);
        },
      });
    };

    return onUpload;
  };

  return (
    <>
      <Grid
        test-id="ps-channel-settings-form-grid"
        id="ps-channel-settings-form-grid"
        gutter="large"
      >
        <GridCell xsmall="12" large="6">
          <PageSection size="small">
            <Stack
              justifyContent="space-between"
              alignItems="center"
              gutter="small"
            >
              <TextSubtitle test-id="ps-channel-details-title">
                <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.channel-details">
                  Channel Details
                </TranslationComponent>
              </TextSubtitle>
              <Stack
                test-id="ps-channel-details-pype-logo-stack"
                alignItems="center"
                gutter="xsmall"
                nowrap
              >
                <Image
                  width="24"
                  height="24"
                  src={PypeLogo as unknown as string}
                />
                <TextBody>Pype</TextBody>
              </Stack>
            </Stack>
            <Spacer />
            <TextBody test-id="ps-channel-name-label" size="small">
              <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.channel-name">
                Channel Name
              </TranslationComponent>
            </TextBody>
            <Spacer size="2xsmall" />
            <Input
              test-id={
                isUpdating
                  ? 'ps-update-channel-name-input'
                  : 'ps-create-channel-name-input'
              }
              placeholder={
                t(
                  'pro-studio/channel-details:form.placeholders.channel-name'
                ) || 'Channel Name'
              }
              value={detailsForm.watch('name')}
              helpText={detailsForm.formState.errors.name?.message}
              hasError={!!detailsForm.formState.errors.name}
              variant="outlined"
              {...detailsForm.register('name', {
                required:
                  t(
                    'pro-studio/channel-details:form.validation-messages.name'
                  ) || 'Channel name is required',
              })}
              // @ts-ignore
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                detailsForm.trigger('name');
                detailsForm.setValue('name', e.target.value, {
                  shouldDirty: true,
                });
              }}
            />
            <Spacer />
            <TextBody test-id="ps-channel-environment-label" size="small">
              <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.environment">
                Environment
              </TranslationComponent>
            </TextBody>
            <Spacer size="2xsmall" />
            <RadioGroup
              value={detailsForm.watch('env')}
              hasError={!!detailsForm.formState.errors.env}
              orientation="horizontal"
              test-id={
                isUpdating
                  ? 'ps-update-channel-environment-radio-group'
                  : 'ps-create-channel-environment-radio-group'
              }
              disabled={isUpdating}
              helpText={
                detailsForm.formState.errors.env?.message
                  ? detailsForm.formState.errors.env?.message
                  : t('pro-studio/channel-details:form.help-texts.env') ||
                    'Cannot be changed after channel is created.'
              }
              {...detailsForm.register('env', {
                required:
                  t(
                    'pro-studio/channel-details:form.validation-messages.env'
                  ) || 'Environment is required',
              })}
              onChange={(e) => {
                const env = e.target.value;
                detailsForm.setValue('env', env, { shouldDirty: true });
                detailsForm.trigger('env');
              }}
            >
              {[...(envs || [])]?.reverse().map((env) => (
                <Radio
                  checked={detailsForm.watch('env') === env}
                  disabled={isUpdating}
                  key={env}
                  value={env}
                >
                  {capitalizeWords(env)}
                </Radio>
              ))}
            </RadioGroup>

            <Spacer size="xlarge" />
            <Divider />
            <Spacer size="xlarge" />

            <form>
              <Stack alignItems="center" justifyContent="space-between">
                <Stack alignItems="center" gutter="small">
                  <TextSubtitle test-id="ps-branding-options-title">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.branding-options">
                      Branding options
                    </TranslationComponent>
                  </TextSubtitle>
                  <Button
                    test-id="ps-channel-branding-options-reset-button"
                    size="small"
                    disabled={!brandingsTouched}
                    variant="ghost"
                    onClick={() => brandingForm.reset()}
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.buttons.reset">
                      Reset
                    </TranslationComponent>
                  </Button>
                </Stack>
                <Button
                  test-id="ps-edit-style-sheet-button"
                  size="small"
                  variant="secondary"
                  onClick={openCssEditorModal}
                >
                  <Icon name="pencil" size="xsmall" slot="prefix" />
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.buttons.edit">
                    Edit Style Sheet
                  </TranslationComponent>
                </Button>
                {cssEditorModal}
              </Stack>
              <Spacer />

              {type === 'update' && (
                <>
                  <TextBody test-id="ps-channel-logo-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.logo">
                      Logo
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <Input
                    test-id="ps-update-channel-logo-input"
                    variant="outlined"
                    placeholder={
                      t('pro-studio/channel-details:form.placeholders.logo') ||
                      'Logo URL'
                    }
                    value={brandingForm.watch('logo')}
                    {...brandingForm.register('logo')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('logo', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                  <TextBody
                    test-id="ps-channel-logo-or-label"
                    size="small"
                    textAlign="center"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.or">
                      or
                    </TranslationComponent>
                  </TextBody>
                  <Filepond
                    test-id="ps-update-channel-logo-filepond"
                    entityType={EntityTypes.ASSET}
                    entityId={detailsForm.getValues('pypeId')}
                    onUpload={getOnUpload('logo')}
                    pictureSrc={brandingForm.watch('logo')}
                    onRemove={() => {
                      brandingForm.setValue('logo', '', {
                        shouldDirty: true,
                      });
                    }}
                    onChange={(p) => {
                      if (!p || !p.data || !p.pictureId) {
                        return;
                      }

                      brandingForm.setValue('logo', '', {
                        shouldDirty: true,
                      });
                    }}
                  />

                  <Spacer size="large" />
                </>
              )}

              <TextBody
                test-id="ps-channel-brand-moment-url-label"
                size="small"
              >
                <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.brand-moment-url">
                  Brand Moment URL
                </TranslationComponent>
              </TextBody>
              <Spacer size="2xsmall" />
              <Input
                test-id={
                  isUpdating
                    ? 'ps-update-channel-brand-moment-url-input'
                    : 'ps-create-channel-brand-moment-url-input'
                }
                variant="outlined"
                placeholder={
                  t(
                    'pro-studio/channel-details:form.placeholders.brand-moment-url'
                  ) || 'Brand Moment URL'
                }
                value={brandingForm.watch('brandMomentUrl')}
                {...brandingForm.register('brandMomentUrl')}
                // @ts-ignore
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { value } = e.target;
                  brandingForm.setValue('brandMomentUrl', value, {
                    shouldDirty: true,
                  });
                }}
              />
              <TextBody
                test-id="ps-channel-brand-moment-url-or-label"
                size="small"
                textAlign="center"
              >
                <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.or">
                  or
                </TranslationComponent>
              </TextBody>
              <Filepond
                test-id={
                  isUpdating
                    ? 'ps-update-channel-brand-moment-url-filepond'
                    : 'ps-create-channel-brand-moment-url-filepond'
                }
                entityType={EntityTypes.ASSET}
                entityId={botId}
                onUpload={getOnUpload('assetData')}
                pictureSrc={brandingForm.watch('brandMomentUrl')}
                onRemove={() => {
                  brandingForm.setValue('brandMomentUrl', '', {
                    shouldDirty: true,
                  });
                }}
                onChange={(p) => {
                  if (!p || !p?.data || !p?.pictureId) {
                    return;
                  }

                  sendProStudioEvent({
                    type: 'updateAssets',
                    // @ts-ignore
                    data: p.data as GetBotByIdData,
                    cb: (assets) => {
                      const asset = assets.find((a) => a.id === p.pictureId);

                      if (asset) {
                        brandingForm.setValue('brandMomentUrl', asset.url, {
                          shouldDirty: true,
                        });
                      }
                    },
                  });
                }}
              />

              <Spacer />
              <Divider />
              <Spacer />

              <Grid>
                <GridCell xsmall="12" large="12">
                  <TextBody test-id="ps-channel-brand-color-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.brand-color">
                      Brand Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-brand-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.brand-color'
                      ) || 'Brand Color'
                    }
                    value={brandingForm.watch('widgetStyleColor')}
                    {...brandingForm.register('widgetStyleColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('widgetStyleColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-solution-bubble-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.solution-bubble-color">
                      Solution Bubble Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-solution-buble-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.solution-bubble-color'
                      ) || 'Solution Bubble Color'
                    }
                    value={brandingForm.watch('agentBubbleColor')}
                    {...brandingForm.register('agentBubbleColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('agentBubbleColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-solution-text-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.solution-text-color">
                      Solution Text Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-solution-buble-text-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.solution-text-color'
                      ) || 'Solution Text Color'
                    }
                    value={brandingForm.watch('agentBubbleTextColor')}
                    {...brandingForm.register('agentBubbleTextColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('agentBubbleTextColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-user-bubble-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.user-bubble-color">
                      User Bubble Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-user-buble-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.user-bubble-color'
                      ) || 'User Bubble Color'
                    }
                    value={brandingForm.watch('userBubbleColor')}
                    {...brandingForm.register('userBubbleColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('userBubbleColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-user-text-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.user-text-color">
                      User Text Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-user-buble-text-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.user-text-color'
                      ) || 'User Text Color'
                    }
                    value={brandingForm.watch('userBubbleTextColor')}
                    {...brandingForm.register('userBubbleTextColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('userBubbleTextColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-button-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.button-color">
                      Button Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-button-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.button-color'
                      ) || 'Button Color'
                    }
                    value={brandingForm.watch('buttonColor')}
                    {...brandingForm.register('buttonColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('buttonColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
                <GridCell xsmall="12" small="6">
                  <TextBody
                    test-id="ps-channel-button-text-color-label"
                    size="small"
                  >
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.button-text-color">
                      Button Text Color
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <ColorPicker
                    test-id="preview-pype-button-text-color"
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.button-text-color'
                      ) || 'Button Text Color'
                    }
                    value={brandingForm.watch('textColor')}
                    {...brandingForm.register('textColor')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      brandingForm.setValue('textColor', value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </GridCell>
              </Grid>
            </form>

            <Spacer size="xlarge" />
            <Divider />
            <Spacer size="xlarge" />

            <form>
              <Stack alignItems="center" gutter="small">
                <TextSubtitle test-id="ps-sizing-options-title">
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.sizing-options">
                    Sizing options
                  </TranslationComponent>
                </TextSubtitle>
                <Button
                  test-id="ps-sizing-options-reset-button"
                  size="small"
                  disabled={!sizingTouched}
                  variant="ghost"
                  onClick={() => sizingForm.reset()}
                >
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.buttons.reset">
                    Reset
                  </TranslationComponent>
                </Button>
              </Stack>
              <Spacer />
              <Stack alignItems="end" gutter="small" nowrap>
                <div style={{ flexGrow: 1 }}>
                  <TextBody test-id="ps-channel-width-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.width">
                      Width
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <Input
                    test-id={
                      isUpdating
                        ? 'ps-update-channel-width-input'
                        : 'ps-create-channel-width-input'
                    }
                    placeholder={
                      t('pro-studio/channel-details:form.placeholders.width') ||
                      'Width'
                    }
                    value={`${sizingForm.watch('widgetWidth')}`}
                    variant="outlined"
                    suffix={
                      t(
                        'pro-studio/channel-details:form.placeholders.pixels'
                      ) || 'Pixels'
                    }
                    {...sizingForm.register('widgetWidth')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const {
                        target: { value },
                      } = e;
                      const ratioLocked =
                        sizingForm.getValues('shouldLockRatio');
                      const width = value ? parseInt(value) : 0;
                      sizingForm.setValue('widgetWidth', width, {
                        shouldDirty: true,
                      });

                      if (ratioLocked) {
                        const height = width
                          ? Math.round(width / widthToHeightRatio)
                          : 0;
                        sizingForm.setValue('widgetHeight', height, {
                          shouldDirty: true,
                        });
                      }
                    }}
                  />
                </div>
                <IconButton
                  test-id="ps-sizing-options-lock-button"
                  size="large"
                  rounded
                  selected={sizingForm.watch('shouldLockRatio')}
                  name={sizingForm.watch('shouldLockRatio') ? 'lock' : 'unlock'}
                  title={
                    sizingForm.watch('shouldLockRatio') ? 'Unlock' : 'Lock'
                  }
                  onClick={() => {
                    sizingForm.setValue(
                      'shouldLockRatio',
                      !sizingForm.watch('shouldLockRatio'),
                      { shouldDirty: true }
                    );
                    setWidthToHeightRatio(
                      getWidthToHeightRatio(
                        +sizingForm.getValues('widgetWidth'),
                        +sizingForm.getValues('widgetHeight')
                      )
                    );
                  }}
                />
                <div style={{ flexGrow: 1 }}>
                  <TextBody test-id="ps-channel-height-label" size="small">
                    <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.height">
                      Height
                    </TranslationComponent>
                  </TextBody>
                  <Spacer size="2xsmall" />
                  <Input
                    test-id={
                      isUpdating
                        ? 'ps-update-channel-height-input'
                        : 'ps-create-channel-height-input'
                    }
                    placeholder={
                      t(
                        'pro-studio/channel-details:form.placeholders.height'
                      ) || 'Height'
                    }
                    value={`${sizingForm.watch('widgetHeight')}`}
                    variant="outlined"
                    suffix={
                      t(
                        'pro-studio/channel-details:form.placeholders.pixels'
                      ) || 'Pixels'
                    }
                    {...sizingForm.register('widgetHeight')}
                    // @ts-ignore
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const {
                        target: { value },
                      } = e;
                      const ratioLocked =
                        sizingForm.getValues('shouldLockRatio');
                      const height = value ? parseInt(value) : 0;
                      sizingForm.setValue('widgetHeight', height, {
                        shouldDirty: true,
                      });

                      if (ratioLocked) {
                        const width = Math.round(height * widthToHeightRatio);
                        sizingForm.setValue('widgetWidth', width, {
                          shouldDirty: true,
                        });
                      }
                    }}
                  />
                </div>
              </Stack>
            </form>

            <Spacer size="xlarge" />
            <Divider />
            <Spacer size="xlarge" />

            <Stack
              justifyContent="space-between"
              alignItems="center"
              gutter="small"
            >
              <TextSubtitle test-id="ps-agent-support-platform-title">
                <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.agent-support-platform">
                  Agent Support Platform
                </TranslationComponent>
              </TextSubtitle>
              <div
                style={{
                  padding: '4px 8px',
                  border: '1px solid rgba(21, 26, 37, .4)',
                  borderRadius: 4,
                }}
              >
                <Stack
                  test-id="ps-agent-support-platform-logo-stack"
                  alignItems="center"
                  gutter="xsmall"
                  nowrap
                >
                  <Image width="16" height="16" src={logos.agentAssist}></Image>
                  <TextCaption variant="primary">Contact Center</TextCaption>
                </Stack>
              </div>
            </Stack>
            <Spacer />
            <BlockList>
              <div>
                <TextBody test-id="ps-account-id-label" size="small">
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.account-id">
                    Account Id
                  </TranslationComponent>
                </TextBody>
                <Spacer size="2xsmall" />
                <Input
                  test-id={
                    isUpdating
                      ? 'ps-update-channel-account-id-input'
                      : 'ps-create-channel-account-id-input'
                  }
                  variant="outlined"
                  value={agentSupportPlatformForm.watch('accountId')}
                  helpText={
                    agentSupportPlatformForm.formState.errors.accountId?.message
                  }
                  // @ts-ignore
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    agentSupportPlatformForm.trigger('accountId');
                    agentSupportPlatformForm.setValue(
                      'accountId',
                      e.target.value,
                      { shouldDirty: true }
                    );
                  }}
                  hasError={
                    !!agentSupportPlatformForm.formState.errors.accountId
                  }
                  {...agentSupportPlatformForm.register('accountId', {
                    validate: (value) => {
                      const inboxId =
                        agentSupportPlatformForm.getValues('inboxId');
                      if (inboxId) {
                        return value
                          ? true
                          : t(
                              'pro-studio/channel-details:form.validation-messages.integration'
                            ) ||
                              'This field is required when other agent support fields are filled';
                      }
                      return true;
                    },
                  })}
                />
              </div>
              <div>
                <TextBody test-id="ps-inbox-id-label" size="small">
                  <TranslationComponent i18nKey="pro-studio/channel-details:form.labels.inbox-id">
                    Inbox Id
                  </TranslationComponent>
                </TextBody>
                <Spacer size="2xsmall" />
                <Input
                  test-id={
                    isUpdating
                      ? 'ps-update-channel-inbox-id-input'
                      : 'ps-create-channel-inbox-id-input'
                  }
                  variant="outlined"
                  value={agentSupportPlatformForm.watch('inboxId')}
                  helpText={
                    agentSupportPlatformForm.formState.errors.inboxId?.message
                  }
                  // @ts-ignore
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    agentSupportPlatformForm.trigger('inboxId');
                    agentSupportPlatformForm.setValue(
                      'inboxId',
                      e.target.value,
                      {
                        shouldDirty: true,
                      }
                    );
                  }}
                  hasError={!!agentSupportPlatformForm.formState.errors.inboxId}
                  {...agentSupportPlatformForm.register('inboxId', {
                    validate: (value) => {
                      const accountId =
                        agentSupportPlatformForm.getValues('accountId');
                      if (accountId) {
                        return value
                          ? true
                          : t(
                              'pro-studio/channel-details:form.validation-messages.integration'
                            ) ||
                              'This field is required when other agent support fields are filled';
                      }
                      return true;
                    },
                  })}
                />
              </div>
            </BlockList>
          </PageSection>
        </GridCell>
        <GridCell xsmall="12" large="6">
          <Sticky top={92} bottomBoundary="#ps-channel-settings-form-grid">
            <PageSection size="small">
              <TextSubtitle test-id="ps-preview-title">
                <TranslationComponent i18nKey="pro-studio/channel-details:form.titles.preview">
                  Preview
                </TranslationComponent>
              </TextSubtitle>
              <Spacer />
              <PypePreview
                logo={brandingForm.getValues('logo')}
                brandMomentUrl={brandingForm.getValues('brandMomentUrl')}
                widgetStyleColor={brandingForm.getValues('widgetStyleColor')}
                widgetStyleTextColor={brandingForm.getValues(
                  'widgetStyleTextColor'
                )}
                agentBubbleColor={brandingForm.getValues('agentBubbleColor')}
                agentBubbleTextColor={brandingForm.getValues(
                  'agentBubbleTextColor'
                )}
                userBubbleColor={brandingForm.getValues('userBubbleColor')}
                userBubbleTextColor={brandingForm.getValues(
                  'userBubbleTextColor'
                )}
                buttonColor={brandingForm.getValues('buttonColor')}
                buttonTextColor={brandingForm.getValues('textColor')}
              />
              <Spacer size="xsmall" />
              <TextBody
                test-id="ps-preview-help-text"
                size="small"
                textAlign="center"
                variant="tertiary"
              >
                <TranslationComponent i18nKey="pro-studio/channel-details:form.help-texts.preview">
                  The position and size of the channel are not reflected in this
                  preview.
                </TranslationComponent>
              </TextBody>
            </PageSection>
          </Sticky>
        </GridCell>
      </Grid>
      <PortalDestination name="channel-settings-form-snackbar-destination" />
      <Snackbar
        test-id="ps-channel-settings-form-snackbar"
        id="channel-settings-form-snackbar-destination"
        open={touched}
      >
        There are unsaved changes
        <Button
          test-id="ps-cancel-channel-settings-btn"
          variant="secondary"
          size="large"
          onClick={onCancel}
          slot="action"
        >
          Cancel
        </Button>
        <Button
          test-id="ps-save-channel-settings-btn"
          size="large"
          onClick={onSubmit}
          disabled={!touched}
          slot="action"
        >
          Save Changes
        </Button>
      </Snackbar>
    </>
  );
};
